/**
 * Copyright (c) 2022, ResMed Inc. All rights reserved.
 */
import {Component, React, useEffect, useState} from 'react';
import FormControlLabel from "@material-ui/core/FormControlLabel";
import Radio from '@material-ui/core/Radio';
import RadioGroup from '@material-ui/core/RadioGroup';
import Accordion from '@material-ui/core/Accordion';
import AccordionSummary from '@material-ui/core/AccordionSummary';
import AccordionDetails from '@material-ui/core/AccordionDetails';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import TextField from "@material-ui/core/TextField";
import FormControl from "@material-ui/core/FormControl";
import {Checkbox, InputLabel, ListItem, ListItemIcon, ListItemText, MenuItem, Select} from "@material-ui/core";
import DeleteIcon from "@material-ui/icons/Delete";
import IconButton from "@material-ui/core/IconButton";
import Typography from "@material-ui/core/Typography";
import '../../../css/views/editors/QuestionEditor.css'
import List from "@material-ui/core/List";
import ListItemButton from '@mui/material/ListItemButton';

/**
 * Default Values for values in Form
 */
const defaultDeviceSerial  = "";
const defaultPatientId     = "";
const defaultDayOffset     = 7;
const defaultOrigin        = "device";
const defaultDate          = new Date().toISOString().replace("Z", "+00:00");
const defaultCategoryLabel = "No Categories";
const defaultAnswerInput   = 0;

/**
 * Keeps track of the last value put in, used to populate newly created questions
 * so the user doesn't have to retype them each time. (Will be defaults the first time around)
 */
let globalDeviceSerial = defaultDeviceSerial;
let globalPatientId    = defaultPatientId;
let globalDayOffset    = defaultDayOffset;
let globalOrigin       = defaultOrigin;
let globalDate         = defaultDate;

// Used on close
let beingDeleted;

/**
 * Component representing the question pop-ups used when editing/creating a response
 *
 * @prop question       current question in list (for default question, pass in { questionId: `id of question definition` })
 * @prop questionIndex  index of current question in list
 * @prop updateQuestion function to call when question is updated
 * @prop deleteQuestion function to call on delete of question
 * @prop dayOffset      value of the selected day offset (if applicable)
 */
export default function QuestionEditor(props) {

    // Used to get question definition
    const questionDefinitions = props.questionDefinitions;

    // Id of current question
    const questionId = props.question.questionId;

    /**
     * Gets the definition of the current question
     *
     * @return current question
     */
    const getCurrentQuestionDefinition = () => {
        let question = null;

        for (let key in questionDefinitions) {

            if (questionDefinitions[key].id === questionId) {
                question = questionDefinitions[key];
            }
        }

        return question;
    }
    let currentQuestionDefinition = getCurrentQuestionDefinition();

    const currentQuestionType = currentQuestionDefinition.type;
    const isSelectionType = (currentQuestionType === "single-selection" || currentQuestionType === "multi-selection");

    const answerOptions = currentQuestionDefinition.answerOptions;

    /**
     * Finds and returns the category given the current radio value
     *
     * @return string
     */
    const findCategory = (answer) => {
        let category;

        // Checks if a category exist for question/answer
        for (let key in answerOptions) {
            let optionValue = answerOptions[key].valueEnum;

            if (optionValue && optionValue === answer) {
                let categories = answerOptions[key].categories;

                if (categories && categories[0]) {
                    category = categories[0];
                }
            }
        }

        return category;
    }

    /**
     * Gets current question
     *
     * @return question
     */
    const getCurrentQuestion = () => {
        if (!props.question.answers) {
            if (isSelectionType) {
                return getQuestion([answerOptions[0].valueEnum], globalDate, globalOrigin, globalDeviceSerial, globalPatientId, globalDayOffset);
            } else {
                return getQuestion([defaultAnswerInput], globalDate, globalOrigin, globalDeviceSerial, globalPatientId, globalDayOffset);
            }
        }

        return props.question;
    }

    /**
     * Handles change in radio group form
     *
     * @param event form event
     */
    const handleRadioGroupChange = (event) => {
        setAnswers([event.target.value]);
    }

    /**
     * Formats the selected date/time properly and sets it as the answer date
     *
     * @param event form event
     */
    const handleDateChange = (event) => {
        setDate(`${event.target.value}+00:00`);
    };

    /**
     * Handles change in Origin form
     *
     * @param event form event
     */
    const handleOriginChange = (event) => {
        setOrigin(event.target.value);
    }

    /**
     * Handles change in Device Serial form
     *
     * @param event form event
     */
    const handleDeviceSerialChange = (event) => {
        setDeviceSerial(event.target.value);
    }

    /**
     * Handles change in Patient Id form
     *
     * @param event form event
     */
    const handlePatientIdChange = (event) => {
        setPatientId(event.target.value);
    }

    /**
     * Handles change in dayOffset form
     *
     * @param event form event
     */
    const handleDayOffsetChange = (event) => {
        //setDayOffset(event.target.value);
    }

    /**
     * Deletes the question
     */
    const deleteQuestion = () => {
        beingDeleted = true;

        props.deleteQuestion(props.questionKey);
    };

    /**
     * Updates the list of answers given an answer
     *
     * @param answer answer to add
     */
    const updateAnswers = (answer) => {
        let newAnswers = Object.assign([], answers);
        newAnswers.push(answer);

        setAnswers(newAnswers);
    }

    /**
     * Deletes an answer in the list
     *
     * @param answer answer to delete
     */
    const deleteAnswer = (answer) => {
        const index = answers.indexOf(answer);

        let newAnswers = Object.assign([], answers);
        newAnswers.splice(index, 1);

        setAnswers(newAnswers);
    }

    /**
     * Resets global values on close.
     */
    useEffect(() => {

        beingDeleted = false;

        return () => {
            if (!beingDeleted) {
                resetGlobal();
            }
        }

    }, [])

    /**
     * Resets all global values to default values
     */
    const resetGlobal = () => {
        globalDate         = defaultDate;
        globalOrigin       = defaultOrigin;
        globalDeviceSerial = defaultDeviceSerial;
        globalPatientId    = defaultPatientId;
        globalDayOffset    = defaultDayOffset;
    }

    /**
     * Sets global values
     *
     * @param date         last used date
     * @param origin       last used origin
     * @param deviceSerial last used device serial
     * @param patientId    last used patient id
     * @param dayOffset    last used dayOffset
     */
    const setGlobal = (date, origin, deviceSerial, patientId, dayOffset) => {
        globalDate         = date;
        globalOrigin       = origin;
        globalDeviceSerial = deviceSerial;
        globalPatientId    = patientId;
        globalDayOffset    = dayOffset;
    }

    /**
     * Generates a new question given the parameters
     *
     * @param radioValue   radio value (answer value)
     * @param date         date of question
     * @param origin       origin of question
     * @param deviceSerial device serial of question
     * @param patientId    patient id of questiom
     * @param dayOffset    day offset of question
     * @return question
     */
    const getQuestion = (currentAnswers, date, origin, deviceSerial, patientId, dayOffset) => {

        let formattedAnswers = [];

        for (let key in currentAnswers) {

            let answer = currentAnswers[key]

            let categories = [];
            const category = findCategory(answer);

            if (category) {
                categories = [category]
            }

            formattedAnswers.push(
                {
                    valueEnum: answer
                }
            )
        }

        let question = {
            questionId: props.question.questionId,
            dateTime: date,
            answers: formattedAnswers,
            source: {
                origin: origin,
                deviceSerial: deviceSerial
            },
            dayOffset: dayOffset
        }

        if (patientId) {
            question.source['patientId'] = patientId
        }

        return question;
    }

    /**
     * Gets current answers depending on the question type
     *
     * @return current answers
     */
    const getCurrentAnswers = () => {
        let answers = []

        for (let key in currentQuestion.answers) {
            let answer = currentQuestion.answers[key];
            answers.push(props.langJson[answer.valueEnum]);
        }

        switch (currentQuestionType) {
            case "single-selection":
                return [answers[0]];

            case "multi-selection":
                return answers;

            default:
                return [answers[0]];
        }
    }

    /**
     * Variables displayed in forms
     */
    const [currentQuestion, setCurrentQuestion] = useState(getCurrentQuestion());

    const [answers, setAnswers]             = useState(getCurrentAnswers)
    const [date, setDate]                   = useState(currentQuestion.dateTime);
    const [origin, setOrigin]               = useState(props.origin ?? currentQuestion.source.origin);
    const [deviceSerial, setDeviceSerial]   = useState(props.deviceSerial ?? currentQuestion.source.deviceSerial)
    const [patientId, setPatientId]         = useState(props.patientId ?? currentQuestion.source.patientId);
    const [dayOffset, setDayOffset]         = useState(props.dayOffset ?? currentQuestion.source.dayOffset);
    const [category, setCategory]           = useState(findCategory(answers[0]));
    const [categoryLabel, setCategoryLabel] = useState(defaultCategoryLabel);

    /**
     * On radio value change, update the UI with new category
     */
    useEffect(() => {

        if (isSelectionType) {
            let category = findCategory(answers[0])

            setCategory(category);
            setCategoryLabel(getCategoryLabel(category));
        }

    }, [answers])

    /**
     * When values change in the form, update the question list accordingly.
     */
    useEffect(() => {

        const question = getQuestion(
            answers,
            date,
            origin,
            deviceSerial,
            patientId,
            dayOffset
        )

        setCurrentQuestion(question);
        setGlobal(date, origin, deviceSerial, patientId, dayOffset);
        props.updateQuestion(question, props.questionKey);

    }, [answers, date, origin, deviceSerial, patientId, dayOffset, category])

    /**
     * Gets the category label that should be displayed
     */
    const getCategoryLabel = (category) => {
        if (category) {
            return category;
        }

        return defaultCategoryLabel;
    }

    /**
     * Gets the correct format for the current question type
     *
     * @param type type of question
     * @return component matching type
     */
    const getCurrentAnswerFormat = (type) => {
        switch (type) {
            case "single-selection":
                return <RadioButtons
                    answerOptions = {answerOptions}
                    onChange      = {handleRadioGroupChange}
                    radioValue    = {answers[0]}
                    langJson      = {props.langJson}
                />

            case "multi-selection":
                return <CheckboxList
                    answerOptions = {answerOptions}
                    updateAnswers = {updateAnswers}
                    deleteAnswer  = {deleteAnswer}
                    answers       = {answers}
                />

            default:
                return <AnswerInput
                    label    = {currentQuestionType}
                    onChange = {handleRadioGroupChange}
                />
        }
    }

    return (
        <Accordion className='questionEditor-wrapper'>
            <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-label="Expand"
                aria-controls="additional-actions1-content"
                id="additional-actions1-header"
            >
                <FormControlLabel
                    aria-label="Acknowledge"
                    className='questionEditor-label'
                    onClick={(event) => event.stopPropagation()}
                    onFocus={(event) => event.stopPropagation()}
                    control={<IconButton color="secondary" aria-label="delete" onClick={() => {
                                deleteQuestion();
                            }}>
                                <DeleteIcon />
                            </IconButton>}
                    label={""}
                />
                <Typography variant="body1" className='questionEditor-headerTitle' >
                    {props.langJson[questionId]}
                </Typography>
                <Typography variant="button" color="secondary" className='questionEditor-categoriesText'>
                    [ {categoryLabel} ]
                </Typography>
            </AccordionSummary>
            <AccordionDetails className='questionEditor-detailsWrapper'>
                {getCurrentAnswerFormat(currentQuestionType)}
                <>
                    <div className='questionEditor-deviceDetails'>
                        <FormControl variant="outlined" className='questionEditor-deviceDetailsInput'>
                            <InputLabel id="origin">Origin</InputLabel>
                            <Select
                                id="origin-selector"
                                defaultValue={origin}
                                onChange={handleOriginChange}
                                label="Origin"
                            >
                                <MenuItem value={"device"}>Device</MenuItem>
                                <MenuItem value={"myair"}>myAir</MenuItem>
                            </Select>
                        </FormControl>
                        <TextField
                            id="deviceSerial"
                            label="Device Serial #"
                            defaultValue={deviceSerial}
                            variant="outlined"
                            className='questionEditor-deviceDetailsInput'
                            onChange={handleDeviceSerialChange}
                        />
                        <TextField
                            id="patientId"
                            label="Patient ID"
                            defaultValue={patientId}
                            variant="outlined"
                            className='questionEditor-deviceDetailsInput'
                            onChange={handlePatientIdChange}
                        />
                        <TextField
                            id="dayOffset"
                            label="Day Offset"
                            defaultValue={dayOffset}
                            variant="outlined"
                            className='questionEditor-deviceDetailsInput'
                            onChange={handleDayOffsetChange}
                        />
                    </div>
                    <div className='questionEditor-responseDateWrapper'>
                        <form noValidate>
                            <TextField
                                id="datetime-local"
                                label="Response Date"
                                type="datetime-local"
                                defaultValue={date.replace("+00:00", "")}
                                InputLabelProps={{
                                    shrink: true,
                                }}
                                onChange={handleDateChange}
                            />
                        </form>
                    </div>
                </>
            </AccordionDetails>
        </Accordion>
    );
}

/**
 * Radio Group for single-selection answers
 *
 * @prop answerOptions list of options
 * @prop radioValue    current selected value
 * @prop onChange      function to call on value change
 */
function RadioButtons(props) {

    let radioButtons = [];

    // Map all possible options
    for (let key in props.answerOptions) {
        let answer = props.answerOptions[key];

        radioButtons.push(
            <FormControlLabel
                name={answer.valueEnum}
                value={answer.valueEnum}
                control={<Radio color="secondary" />}
                label={props.langJson[answer.valueEnum]}
                labelPlacement="end"
                key={answer.valueEnum}
            />
        )
    }

    return (
        <RadioGroup
            className='questionEditor-answers'
            row aria-label="position"
            name="position"
            value={props.radioValue}
            onChange={props.onChange}
        >
            {radioButtons}
        </RadioGroup>
    );
}

/**
 * Text Field for text answers
 *
 * @props label    label used to describe text field
 * @props onChange function to call on text field change
 */
function AnswerInput(props) {
    return (
        <TextField
            id="answerInput"
            label={props.label}
            defaultValue={0}
            variant="outlined"
            onChange={props.onChange}
            style={{'marginBottom': 10}}
        /> );
}

/**
 * Checkbox list for multi-selection answers
 *
 * @prop answerOptions list of options
 * @prop deleteAnswer  function to call on unchecked answer
 * @prop updateAnswers function to call on checked answer
 */
function CheckboxList(props) {
    const [checked, setChecked] = useState(props.answers);

    /**
     * Handles checkbox toggle
     *
     * @param value value that was checked
     */
    const handleToggle = (value) => () => {
        const currentIndex = checked.indexOf(value);
        const newChecked = [...checked];

        if (currentIndex === -1) {
            newChecked.push(value);
            props.updateAnswers(value);
        } else {
            newChecked.splice(currentIndex, 1);
            props.deleteAnswer(value);
        }

        setChecked(newChecked);
    };

    let listItems = [];

    // Maps answers to checkboxes
    for (let key in props.answerOptions) {
        let answer = props.answerOptions[key]

        let labelId = "checkbox-" + answer.valueEnum

        listItems.push(
            <ListItem key={answer.valueEnum} style={{padding: 0}}>
                <ListItemButton role={undefined} onClick={handleToggle(answer.valueEnum)} dense>
                    <ListItemIcon>
                        <Checkbox
                            edge="start"
                            checked={checked.indexOf(answer.valueEnum) !== -1}
                            tabIndex={-1}
                            disableRipple
                        />
                    </ListItemIcon>
                    <ListItemText id={labelId} primary={answer.valueEnum} />
                </ListItemButton>
            </ListItem>
        )
    }

    return (
        <List style={{ display: 'flex', flexDirection: 'row'}} >
            {listItems}
        </List>
    );
}
